import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import logoText from '@assets/logo.png';
import { Card } from '@components/atoms/Card';
import { Loading } from '@components/atoms/Loading';
import { SetupForm } from '@components/organisms/SetupForm';
import { LinkProblemPanel } from '@components/molecules/LinkProblemPanel';
import { ResendConfirmation } from '@components/molecules/ResendConfirmation';
import { ErrorRetryPanel } from '@components/molecules/ErrorRetryPanel';
import { SetupDoneMobileWeb } from '@components/molecules/SetupDoneMobileWeb';
import {
  useSetupComplete,
  useSetupResend,
  useSetupVerify,
} from '@hooks/api/auth/useSetup';
import useLogin from '@hooks/api/auth/useLogin';
import { useTranslation } from '@hooks/useTranslation';
import { APP_STORE_URL, PLAY_STORE_URL } from '@pages/mobileOnly/mobileOnly';

const MOBILE_MAX_WIDTH = 768;
const RESEND_COOLDOWN_SECONDS = 60;

type Terminal = 'expired' | 'used' | 'invalid' | 'done' | 'signInFailed';

const httpStatusOf = (error: unknown): number | undefined =>
  (error as { response?: { status?: number } })?.response?.status;

/**
 * Sprint 47, story-232. `/setup?token=...`: the one-time account setup page.
 *
 * Email-link scanners fetch URLs, so this page only VERIFIES on load (read-only, once per mount);
 * the token is consumed solely by the explicit Save, which posts to `/auth/setup/complete`. The
 * token lives in component state only: never storage, analytics or the console. A missing token
 * goes straight to the "isn't valid" panel with no request.
 *
 * Success: viewport > 768 starts a session exactly as the login page does and replaces the
 * history entry (Back never returns to a spent link); viewport <= 768 shows "You're all set" with
 * the store links and stores nothing (the web app blocks small viewports).
 */
const SetupPage: FC = () => {
  const tSetup = useTranslation('setup');
  const location = useLocation();
  const history = useHistory();
  const { loginWithToken } = useLogin();

  // Captured once so a later URL change cannot trigger a second verify.
  const [token] = useState(() => new URLSearchParams(location.search).get('token') ?? '');

  const verify = useSetupVerify(token);
  const complete = useSetupComplete();
  const resend = useSetupResend();

  const [terminal, setTerminal] = useState<Terminal | null>(null);
  const [policyMessage, setPolicyMessage] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [resendSent, setResendSent] = useState(false);
  const [resendError, setResendError] = useState<string | null>(null);
  const [cooldown, setCooldown] = useState(0);
  const headingFocused = useRef<string | null>(null);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const timer = window.setTimeout(() => setCooldown((value) => value - 1), 1000);
    return () => window.clearTimeout(timer);
  }, [cooldown]);

  const goToSignIn = useCallback(() => history.push('/login'), [history]);

  const view = useMemo((): Terminal | 'loading' | 'valid' | 'error' => {
    if (terminal) return terminal;
    if (!token) return 'invalid';
    if (verify.isError) return 'error';
    if (verify.isPending || !verify.data) return 'loading';
    switch (verify.data.status) {
      case 'VALID':
        return 'valid';
      case 'EXPIRED':
        return 'expired';
      case 'USED':
        return 'used';
      default:
        return 'invalid';
    }
  }, [terminal, token, verify.isError, verify.isPending, verify.data]);

  // Move focus to the heading whenever the visible state changes (screen-reader announcement).
  useEffect(() => {
    if (headingFocused.current === view) return;
    headingFocused.current = view;
    document.querySelector<HTMLElement>('[data-setup-card] h1')?.focus();
  }, [view, resendSent]);

  const handleSave = async (password: string) => {
    setPolicyMessage(null);
    setSubmitError(null);
    try {
      const result = await complete.mutateAsync({ token, password });
      if (result?.token) {
        if (window.innerWidth > MOBILE_MAX_WIDTH) {
          await loginWithToken(result.token, false, true);
        } else {
          // Discard the token: no web session on a viewport the web app blocks.
          setTerminal('done');
        }
        return;
      }
      switch (result?.errorCode) {
        case 'PASSWORD_POLICY':
          setPolicyMessage(result.errorMessage ?? null);
          return;
        case 'TOKEN_EXPIRED':
          setTerminal('expired');
          return;
        case 'TOKEN_USED':
          setTerminal('used');
          return;
        case 'TOKEN_INVALID':
          setTerminal('invalid');
          return;
        case 'SIGN_IN_FAILED':
          setTerminal('signInFailed');
          return;
        default:
          setSubmitError(tSetup.networkError);
      }
    } catch (error) {
      setSubmitError(
        httpStatusOf(error) === 429 ? tSetup.rateLimited : tSetup.networkError,
      );
    }
  };

  const handleResend = async (email: string) => {
    setResendError(null);
    try {
      await resend.mutateAsync(email);
    } catch (error) {
      if (httpStatusOf(error) === 429) {
        setResendError(tSetup.rateLimited);
        return;
      }
      // Any other outcome shows the same generic confirmation (no enumeration).
    }
    setResendSent(true);
    setCooldown(RESEND_COOLDOWN_SECONDS);
  };

  const renderBody = () => {
    if (view === 'loading') {
      return (
        <div className="flex flex-col items-center gap-3 py-6" aria-live="polite">
          <Loading />
          <p className="text-gray-600 dark:text-gray-300">{tSetup.checking}</p>
        </div>
      );
    }
    if (view === 'error') {
      return <ErrorRetryPanel onRetry={() => verify.refetch()} />;
    }
    if (view === 'done') {
      return (
        <SetupDoneMobileWeb appStoreUrl={APP_STORE_URL} playStoreUrl={PLAY_STORE_URL} />
      );
    }
    if (view === 'signInFailed') {
      return (
        <div className="flex flex-col gap-4" data-testid="setup-sign-in-failed">
          <h1 tabIndex={-1} className="text-xl font-bold text-gray-900 dark:text-white">
            {tSetup.signInFailed}
          </h1>
          <button
            type="button"
            onClick={goToSignIn}
            className="text-sm text-indigo-600 hover:underline dark:text-indigo-300">
            {tSetup.goToSignIn}
          </button>
        </div>
      );
    }
    if (view === 'valid' && verify.data?.status === 'VALID') {
      return (
        <div className="flex flex-col gap-6">
          <div className="flex flex-col gap-2">
            <h1 tabIndex={-1} className="text-2xl font-bold text-gray-900 dark:text-white">
              {tSetup.title}
            </h1>
            <p className="text-gray-500 dark:text-gray-400">{tSetup.subtitle}</p>
          </div>
          <SetupForm
            email={verify.data.email}
            isSubmitting={complete.isPending}
            onSubmit={handleSave}
            serverPolicyMessage={policyMessage}
            submitError={submitError}
            onPasswordEdited={() => setPolicyMessage(null)}
          />
        </div>
      );
    }
    if (resendSent) {
      return <ResendConfirmation cooldownSeconds={cooldown} onGoToSignIn={goToSignIn} />;
    }
    return (
      <LinkProblemPanel
        reason={view === 'expired' || view === 'used' ? view : 'invalid'}
        onResend={handleResend}
        isSending={resend.isPending || cooldown > 0}
        errorMessage={resendError}
        onGoToSignIn={goToSignIn}
      />
    );
  };

  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-6 bg-indigo-950 p-4 dark:bg-neutral-900">
      {/* Decorative: the page heading names the page. */}
      <img src={logoText} alt="" className="w-full max-w-xs" />
      <Card className="w-full max-w-md" data-setup-card>
        {renderBody()}
      </Card>
    </div>
  );
};

export default SetupPage;
